<template>
  <v-data-table
    :items="deviceList"
    :headers="fiteredTableHeaders"
    :loading="loading"
    :search="search"
    :footer-props="{
      showFirstLastPage: true,
      firstIcon: 'mdi-arrow-collapse-left',
      lastIcon: 'mdi-arrow-collapse-right',
      prevIcon: 'mdi-minus',
      nextIcon: 'mdi-plus'
    }"
    class="elevation-1 admin-table"
    loading-text="Loading... Please wait"
  >

    <template v-slot:item.status="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-avatar
              :color="statusColor(item.id)"
              size="20"
              v-bind="attrs"
              v-on="on"
            >
            </v-avatar>
          </template>
          <span>{{ getCurrentStatus(item.id) }}</span>
        </v-tooltip>
      </template>

    <template v-slot:item.actions="{item}">
      <div class="reports-action-launch" @click="launchRowAction">
        <v-icon>mdi-dots-vertical</v-icon>
        <ActionContainer :ref="item.key" :heading="'Action'" class="report-action-options">
          <ActionItem 
            :disabled="!$can('unassignDeviceFromVehicle')"
            label="Unassign"
            icon="mdi-link"
            @click.native="unassignDevice(item)"
          />
          <ActionItem
            :disabled="!$can('viewDeviceInfo')"
            label="Device Config"
            icon="mdi-settings"
            @click.native="toggleDeviceConfig(item)"
          />
          <ActionItem 
            label="Retrieve Logs"
            icon="mdi-file"
            @click.native="toggleLogComp(item)"/>
          <ActionItem 
            :disabled="!$can('updateVehicle')"
            label="Edit Vehicle"
            icon="mdi-pencil"
            @click.native="toggleConfigComponent(item, 'vehicle')"/>

          <ActionItem 
            :disabled="!$can('viewDeviceInfo')"
            icon="mdi-laptop"
            label="Device Info"
            @click.native="toggleConfigComponent(item, 'device')"/>

          <ActionItem 
            :disabled="!$can('editAlertConfig')"
            icon="mdi-bell"
            label="Alert Config"
            @click.native="toggleConfigComponent(item, 'alert')"/>
          <ActionItem 
            label="Format Disk"
            icon="mdi-harddisk"
            @click.native="toggleFormat(item)"
          />
          <ActionItem 
            label="Reboot Device"
            icon="mdi-power"
            @click.native="toggleReboot(item)"/>
          <ShowOnMap :device="item.id" :org="org" />
          <ActionItem
            :disabled="!$can('createFirmwareUpdateJob') && !$can('createDeviceConfigJob') && !$can('createDeviceInstallJob')"
            label="Create Job"
            icon="mdi-clipboard-list"
            @click.native="toggleJobCreation(item)"/>
        </ActionContainer>
      </div>
    </template>
  </v-data-table>
</template>

<script>

import ActionContainer from '@/components/general/ActionContainer.vue';
import ActionItem from '@/components/general/ActionItem.vue';
import ShowOnMap from '@/components/general/menu/ShowOnMap.vue';
import { getColor } from '@/utils';
import { mapGetters } from "vuex";


export default {
  name: 'AssignedDevicesTable',
  components: {
    ActionContainer,
    ActionItem,
    ShowOnMap
  },
  props: {
    data: {
      type: Array,
      required: true
    },
    search: {
      type: String,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    },
    // we use this just for the show on map feature to launch the customer site
    org: {
      type: String,
      required: true
    },
  },
  data() {
    return {
      headers: [
        {
          text: 'IMEI',
          value: 'imei',
          align: 'left'
        },
        {
          text: 'Install Tag',
          value: 'install_tag',
          align: 'left'
        },
        {
          text: 'Status',
          value: 'status',
          align: 'left'
        },
        {
          text: 'Vehicle',
          value: 'vehicle',
          align: 'left'
        },
        {
          text: 'Model',
          value: 'model',
          align: 'left'
        },
        {
          text: 'Firmware',
          value: 'firmware',
          align: 'left'
        },
        {
          text: 'Actions',
          value: 'actions',
          align: 'left'
        }
      ],
      selected: []
    }
  },
  computed: {
    ...mapGetters('presence', [
      'vehicleStatus',
      'devicesMap'
    ]),
    fiteredTableHeaders () {
				return this.headers.filter(i => !this.hideHeader[i.value])
			},
			hideHeader () {
				return {
					status: !this.$can('getVehiclePresence')
				}
			},
    deviceList() {
      return this.data.map(item => ({ ...item, status: this.statusLabel(item.id) }));
    }
  },
  watch: {
    vehicleStatus() {
      this.data = this.data.map(item => ({ ...item, status: this.statusLabel(item.id) }));
    }
  },
  methods: {
    getCurrentStatus(id) {
      let exists = this.devicesMap[id];
      let map = {
        "UNKNOWN": "Unknown",
        "OFFLINE": "Offline",
        "STATIONARY": "Park Mode",
        "IDLING": "Idling",
        "MOVING": 'Driving'
      }
      
      if (exists) {
        let status = this.vehicleStatus[this.devicesMap[id].vehicle_id];

        return map[status]
      }
      else {
        return 'Unknown';
      }
    },
    launchRowAction(e) {
      let allOptions = document.querySelectorAll(".report-action-options");

      for (let option of allOptions) {
        option.classList.remove('active-options');
        if (option === e.target.nextSibling) {
          option.classList.toggle('active-options');
        } 
      }
    },
    toggleJobCreation(item) {
      this.$emit('toggleJobCreation', item);
    },  
    toggleConfigComponent(item, display) {
      this.$emit('toggleConfigComponent', item, display);
    },
    toggleFormat(item) {
      this.$emit('toggleFormat', item);
    },
    unassignDevice(item) {
      this.$emit('unassignDevice', item);
    },
    showInfoModal(item) {
      this.$emit('showInfoModal', item);
    },
    toggleReboot(item) {
      this.$emit('toggleReboot', item);
    },
    toggleAssignmentComp() {
      this.$emit('toggleAssignmentComp');
    },
    toggleDeviceConfig(item) {
      this.$emit('toggleDeviceConfig', item);
    },
    toggleLogComp(item) {
      this.$emit('toggleLogComp', item);
    },
    statusColor(id) {
      let exists = this.devicesMap[id];
   
      if (exists) {
        let device = this.vehicleStatus[this.devicesMap[id].vehicle_id];

        return getColor(device)
      }
      else {
        return getColor('unknown');
      }
      
    },
    statusLabel(id) {
      let exists = this.devicesMap[id];

      if (exists) {
        let label = this.vehicleStatus[this.devicesMap[id].vehicle_id];

        return label;
      }
      else {
        return 'UNKNOWN';
      }
    }
  }
}
</script>

<style lang="scss">

</style>